import { Button, ButtonStyle } from '@davidlloydleisure/common-js-frontend';
import type { ErrorInfo, ReactNode } from 'react';
import React, { Component, use } from 'react';

import { SiteContext } from 'src/context/site-context';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import {
  TranslatedStaticText,
  useStaticTextTranslator,
} from 'src/translators/static-text-translator';

import * as style from './page-error-boundary.module.scss';

const handleRetry = (): void => {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

const LocalisedErrorFallback: React.FC = () => {
  const { buildType } = use(SiteContext);
  const translate = useStaticTextTranslator();

  return (
    <div className={style.errorContainer}>
      <h1 className={style.errorTitle}>
        <TranslatedStaticText lookupKey="somethingHasGoneWrong" />
      </h1>
      <p>
        <TranslatedStaticText lookupKey="pleaseRefreshOrTryAgainLater" />
      </p>
      <Button
        brand={getBrandForBuildType(buildType)}
        style={ButtonStyle.PRIMARY}
        text={translate('retry')}
        onClick={handleRetry}
      />
    </div>
  );
};

const StaticErrorFallback: React.FC = () => (
  <div className={style.errorContainer}>
    <h1 className={style.errorTitle}>Something has gone wrong</h1>
    <p>Please refresh the page or try again later.</p>
    <button type="button" onClick={handleRetry}>
      Retry
    </button>
  </div>
);

type BaseErrorBoundaryProps = {
  children: ReactNode;
};

type BaseErrorBoundaryState = {
  hasError: boolean;
};

abstract class BaseErrorBoundary extends Component<BaseErrorBoundaryProps, BaseErrorBoundaryState> {
  state: BaseErrorBoundaryState = { hasError: false };

  protected abstract readonly boundaryName: string;

  protected abstract renderFallback(): ReactNode;

  static getDerivedStateFromError(): BaseErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[${this.boundaryName}] Uncaught error:`, error, errorInfo);

    if (typeof window !== 'undefined') {
      window.newrelic?.noticeError(error, {
        source: this.boundaryName,
        componentStack: errorInfo.componentStack ?? 'unknown',
      });
    } else {
      throw error;
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.renderFallback();
    }
    return this.props.children;
  }
}

export class StaticErrorBoundary extends BaseErrorBoundary {
  protected readonly boundaryName = 'StaticErrorBoundary';

  protected renderFallback(): ReactNode {
    return <StaticErrorFallback />;
  }
}

export class LocalisedErrorBoundary extends BaseErrorBoundary {
  protected readonly boundaryName = 'LocalisedErrorBoundary';

  protected renderFallback(): ReactNode {
    return <LocalisedErrorFallback />;
  }
}
